import {
  AfterViewInit,
  Component,
  effect,
  input,
  isDevMode,
  model,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import {
  ASSISTANT_DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS,
  GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
} from '@shared';
import type * as fromAppI18n from '../../../app.i18n';
import { FileUploadV2Store } from '../../shared/directives/file-upload-v2';
import { TranslationLanguageContextDirective } from '../../shared/directives/translation-language-context.directive';
import {
  AssistantBuilderFileUploadComponent,
  AssistantBuilderFileValue,
} from './assistant-builder-file-upload.component';
import { skillMdConfigurationFileValidator } from './assistant-builder-skill-md-config.validators';
import { AssistantBuilderTraitGenericFilesValue } from './assistant-builder-trait-generic-form.validators';
import { TrimOnBlurDirective } from './trim-on-blur.directive';

export type AssistantBuilderTraitComplianceForm = {
  [K in keyof AssistantBuilderTraitComplianceFormValues]: FormControl<
    AssistantBuilderTraitComplianceFormValues[K]
  >;
};

/**
 * Compliance authors a skill.md like generic/comparison (SB-3325 T4/KD-4):
 * the three free-text instruction fields are replaced by the configuration
 * file; the behavioral toggles and the per-locale labels stay.
 *
 * SB-3325 PR-4 (KD-21 / D14–D15): the two per-locale labels are the OPTIONAL
 * "Customize the upload text" fields — decoupled from the reference-documents
 * toggle. Blank means "use the per-language default" (materialized on save by
 * the page's `buildTranslation`); the placeholder shows that default. The page
 * renders one instance per slot (und/fr/en), so the labels are three-slot
 * no-carry exactly like the basic fields.
 *
 * SB-3325 PR-5: the two upload zones are bound to PAGE-OWNED shared stores
 * (`configurationUploadStore` / `referenceUploadStore`), one instance shared by
 * all three slots — so chips, upload state, and validator errors are identical
 * across und/fr/en and gate Save cross-slot. The inputs are `required`: an
 * unbound store fails loud at first render (closed-loop invariant #2).
 */
export interface AssistantBuilderTraitComplianceFormValues {
  removePromptInput: boolean;
  userWillUploadReferenceDocuments: boolean;
  configurationFile: AssistantBuilderTraitGenericFilesValue;
  files: AssistantBuilderFileValue[];
  /** Translatable, optional: custom upload text for reference documents (per-locale). */
  referenceLabel: string;
  /** Translatable, optional: custom upload text for the document to review (per-locale). */
  regularLabel: string;
}

@Component({
  selector: 'squadbox-assistant-builder-trait-compliance-form',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    fromSeriousSystem.ToggleComponent,
    fromSeriousSystem.InputComponent,
    AssistantBuilderFileUploadComponent,
    TrimOnBlurDirective,
    TranslationLanguageContextDirective,
  ],
  template: `
    @let form = this.form();
    <form class="flex flex-col gap-4" [formGroup]="form">
      <!-- skill.md configuration file (Instructions) FIRST — above Knowledge per
           Figma (§9); mirrors the generic form. -->
      <div class="flex flex-col gap-2">
        <squadbox-assistant-builder-file-upload
          #configZone
          [label]="
            'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.CONFIGURATION_FILE.LABEL'
              | translate
          "
          [placeholder]="
            'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.CONFIGURATION_FILE.PLACEHOLDER'
              | translate
          "
          filePickerButtonIcon="plus"
          formControlName="configurationFile"
          [fileSupportedExtensions]="configurationFileSupportedExtensions"
          [transactionId]="fileUploadTransactionId()"
          [uploadStore]="configurationUploadStore()"
        ></squadbox-assistant-builder-file-upload>
        <ul class="typo-caption text-neutral-700 mt-0 space-y-1 list-disc pl-5">
          <li
            [innerHTML]="
              'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.CONFIGURATION_FILE.HINT'
                | translate: configurationFileTranslateParams
            "
          ></li>
        </ul>
      </div>

      <!-- Divider (above the reference documents / Knowledge) -->
      <div class="py-2"><div class="border-t border-neutral-200"></div></div>

      <!-- Admin-provided reference documents — single labeled field per the
           Skill Template Configurations spec (label "Reference documents",
           placeholder "Add files"), not a separate header. When the toggle
           below is off at least one is required; when on these are optional. -->
      <squadbox-assistant-builder-file-upload
        #referenceZone
        [label]="
          'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.SECTION_REFERENCE_DOCUMENTS'
            | translate
        "
        [placeholder]="
          'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.FILES.PLACEHOLDER' | translate
        "
        filePickerButtonIcon="plus"
        formControlName="files"
        [fileSupportedExtensions]="documentUploadSupportedExtensions"
        [transactionId]="fileUploadTransactionId()"
        [uploadStore]="referenceUploadStore()"
      ></squadbox-assistant-builder-file-upload>

      <!-- Divider -->
      <div class="py-2"><div class="border-t border-neutral-200"></div></div>

      <!-- Let users add their own reference documents (D14) — small toggle,
           divider-separated section rhythm per Figma (11531:14018). -->
      <sd-toggle
        size="sm"
        formControlName="userWillUploadReferenceDocuments"
        [label]="
          'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.USER_WILL_UPLOAD_REFERENCE_DOCUMENTS.LABEL'
            | translate
        "
        [subtext]="
          'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.USER_WILL_UPLOAD_REFERENCE_DOCUMENTS.DESCRIPTION'
            | translate
        "
      />

      <!-- Divider -->
      <div class="py-2"><div class="border-t border-neutral-200"></div></div>

      <!-- Customize the upload text (D15): two OPTIONAL fields; blank uses the
           per-language default. Rendered ONCE (the hidden secondary-locale slot
           passes renderCustomizeText=false, so it isn't repeated) — in
           multilingual mode the FR (primary) + EN (secondary) inputs stack under
           one numbered gutter, per Figma (11531:14046). -->
      @if (renderCustomizeText()) {
        <div class="flex flex-col gap-2">
          <p
            class="typo-caption font-semibold leading-4 text-neutral-700 px-1 py-0.5"
          >
            {{
              'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.CUSTOMIZE_UPLOAD_TEXT.LABEL'
                | translate
            }}
          </p>
          <p class="typo-caption text-neutral-500 leading-4 px-1">
            {{
              'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.CUSTOMIZE_UPLOAD_TEXT.HELPER'
                | translate
            }}
          </p>

          <!-- Field 1 — reference-documents label. The ordinal sits in a 24px
               gutter aligned to the first input's center; the per-locale
               placeholder default resolves via each input's own language
               context. SBB-16 control-disables these inputs when the
               reference-documents toggle is OFF; sd-input then renders the Figma
               disabled treatment (placeholder + FR/EN tag greyed to neutral-400),
               so no group-level styling is needed here. -->
          <div class="flex items-start gap-1">
            <span
              class="w-6 shrink-0 pt-4 text-center typo-caption text-neutral-400"
              aria-hidden="true"
              >1</span
            >
            <div class="flex-1 min-w-0 flex flex-col gap-2">
              <div [squadboxTranslationLanguageContext]="language()">
                <sd-input
                  squadboxTrimOnBlur
                  formControlName="referenceLabel"
                  [label]="''"
                  [leadingLabel]="isMultilingual() ? localeTag() : ''"
                  [placeholder]="
                    'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.REFERENCE_LABEL.DEFAULT'
                      | translate
                  "
                />
              </div>
              @if (secondaryForm(); as sec) {
                <div
                  [squadboxTranslationLanguageContext]="
                    secondaryLanguage() ?? language()
                  "
                >
                  <sd-input
                    squadboxTrimOnBlur
                    [formControl]="sec.controls.referenceLabel"
                    [label]="''"
                    [leadingLabel]="secondaryLocaleTag()"
                    [placeholder]="
                      'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.REFERENCE_LABEL.DEFAULT'
                        | translate
                    "
                  />
                </div>
              }
            </div>
          </div>

          <!-- Field 2 — document-to-review label. -->
          <div class="flex items-start gap-1">
            <span
              class="w-6 shrink-0 pt-4 text-center typo-caption text-neutral-400"
              aria-hidden="true"
              >2</span
            >
            <div class="flex-1 min-w-0 flex flex-col gap-2">
              <div [squadboxTranslationLanguageContext]="language()">
                <sd-input
                  squadboxTrimOnBlur
                  formControlName="regularLabel"
                  [label]="''"
                  [leadingLabel]="isMultilingual() ? localeTag() : ''"
                  [placeholder]="
                    'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.REGULAR_LABEL.DEFAULT'
                      | translate
                  "
                />
              </div>
              @if (secondaryForm(); as sec) {
                <div
                  [squadboxTranslationLanguageContext]="
                    secondaryLanguage() ?? language()
                  "
                >
                  <sd-input
                    squadboxTrimOnBlur
                    [formControl]="sec.controls.regularLabel"
                    [label]="''"
                    [leadingLabel]="secondaryLocaleTag()"
                    [placeholder]="
                      'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.REGULAR_LABEL.DEFAULT'
                        | translate
                    "
                  />
                </div>
              }
            </div>
          </div>
        </div>
      }

      <!-- Divider -->
      <div class="py-2"><div class="border-t border-neutral-200"></div></div>

      <!-- Disable message input — LAST section per Figma (11531:14018), small
           toggle matching the multilingual switch. -->
      <sd-toggle
        size="sm"
        formControlName="removePromptInput"
        [label]="
          'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.REMOVE_PROMPT_INPUT.LABEL'
            | translate
        "
        [subtext]="
          'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.REMOVE_PROMPT_INPUT.DESCRIPTION'
            | translate
        "
      />
    </form>
  `,
})
export class AssistantBuilderTraitComplianceFormComponent implements AfterViewInit {
  // Input/Output properties
  public readonly language = input.required<fromAppI18n.SupportedLanguage>();
  /**
   * When true, the customize-text fields render a leading locale tag (FR/EN) so
   * the stacked multilingual slots are distinguishable. The single-language
   * (und) slot passes false and shows no tag.
   */
  public readonly isMultilingual = input<boolean>(false);
  /**
   * Whether this instance renders the shared "Customize the upload text" section.
   * The primary slot renders it once (and also hosts the secondary locale's
   * inputs via `secondaryForm`); the hidden secondary slot passes false so the
   * section — and its label controls' DOM accessors — aren't duplicated.
   */
  public readonly renderCustomizeText = input<boolean>(true);
  /**
   * In multilingual mode, the secondary locale's form (EN when this primary slot
   * is FR): its `referenceLabel`/`regularLabel` controls are rendered inline
   * beneath the primary's, so the two Customize fields aren't a repeated section.
   * Null in single-language mode.
   */
  public readonly secondaryForm =
    input<FormGroup<AssistantBuilderTraitComplianceForm> | null>(null);
  /** Language context for the secondary-locale inputs (for the placeholder default). */
  public readonly secondaryLanguage =
    input<fromAppI18n.SupportedLanguage | null>(null);
  public readonly fileUploadTransactionId = input.required<string>();
  /**
   * Page-owned shared stores (SB-3325 PR-5). Required: an unbound store fails
   * loud at first render rather than a zone silently creating an orphan store
   * (closed-loop invariant #2). One instance per LOGICAL zone, shared by all
   * three und/fr/en slots.
   */
  public readonly configurationUploadStore =
    input.required<FileUploadV2Store>();
  public readonly referenceUploadStore = input.required<FileUploadV2Store>();

  private readonly configZone =
    viewChild<AssistantBuilderFileUploadComponent>('configZone');
  private readonly referenceZone =
    viewChild<AssistantBuilderFileUploadComponent>('referenceZone');

  // Readonly properties
  public readonly configurationFileTranslateParams = {
    configFile: GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
  } as const;
  public readonly configurationFileSupportedExtensions = ['md'];
  public readonly documentUploadSupportedExtensions: string[] = [
    ...ASSISTANT_DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS,
  ];

  // Form with cross-field validator for files
  public readonly form = model<FormGroup<AssistantBuilderTraitComplianceForm>>(
    new FormGroup<AssistantBuilderTraitComplianceForm>(
      {
        removePromptInput: new FormControl(false, {
          nonNullable: true,
        }),
        userWillUploadReferenceDocuments: new FormControl(false, {
          nonNullable: true,
        }),
        configurationFile:
          new FormControl<AssistantBuilderTraitGenericFilesValue>(
            {
              value: [],
              disabled: false,
            },
            { nonNullable: true },
          ),
        files: new FormControl<AssistantBuilderFileValue[]>(
          {
            value: [],
            disabled: false,
          },
          { nonNullable: true },
        ),
        // KD-21/D15: the customize-text labels are OPTIONAL (no required/min
        // validators) and decoupled from userWillUploadReferenceDocuments —
        // blank persists the per-language default (see page.buildTranslation).
        referenceLabel: new FormControl('', { nonNullable: true }),
        regularLabel: new FormControl('', { nonNullable: true }),
      },
      {
        validators: [
          complianceFilesValidator,
          skillMdConfigurationFileValidator('configurationFile', ['files']),
        ],
      },
    ),
  );

  /**
   * SBB-16: the reference-label customize field only applies when users will
   * upload their own reference documents. Track the toggle so the field can be
   * disabled while it is OFF (per Figma). getRawValue() (the page save path)
   * still reads a disabled control, so a blank field keeps its per-language
   * default.
   */
  private readonly userWillUploadReferenceDocuments = toSignal(
    this.form().controls.userWillUploadReferenceDocuments.valueChanges,
    {
      initialValue: this.form().controls.userWillUploadReferenceDocuments.value,
    },
  );

  constructor() {
    // SBB-16: disable the reference-label customize field(s) whenever the
    // "users will upload reference documents" toggle is OFF — this slot's field
    // and the inlined secondary-locale field (multilingual). No effect on save:
    // the page reads the group via getRawValue(), which includes disabled
    // controls, and referenceLabel has no validators so validity is unchanged.
    effect(() => {
      const enabled = this.userWillUploadReferenceDocuments();
      const controls = [
        this.form().controls.referenceLabel,
        this.secondaryForm()?.controls.referenceLabel,
      ];
      for (const control of controls) {
        if (!control) {
          continue;
        }
        if (enabled) {
          control.enable({ emitEvent: false });
        } else {
          control.disable({ emitEvent: false });
        }
      }
    });
  }

  /**
   * Dev-mode structural guard (closed-loop invariant #2): every zone must have
   * resolved to a page-owned shared store, never its own factory instance — an
   * orphan store on a compliance zone silently laundries an un-uploaded file
   * into `upload-done` via the value mirror.
   */
  ngAfterViewInit(): void {
    if (!isDevMode()) return;
    const configStore = this.configZone()?.getResolvedStore();
    const referenceStore = this.referenceZone()?.getResolvedStore();
    if (
      configStore !== this.configurationUploadStore() ||
      referenceStore !== this.referenceUploadStore()
    ) {
      throw new Error(
        'Compliance file-upload zones must resolve to the page-owned shared ' +
          'stores (configurationUploadStore / referenceUploadStore). A zone ' +
          'that created its own store would split the compliance shared-store ' +
          'topology (SB-3325 PR-5 closed-loop invariant #2).',
      );
    }
  }

  /** Uppercase locale tag shown as the leading label in multilingual mode. */
  public localeTag(): string {
    return this.language().toUpperCase();
  }

  /** Uppercase tag for the inlined secondary-locale inputs (empty if none). */
  public secondaryLocaleTag(): string {
    return this.secondaryLanguage()?.toUpperCase() ?? '';
  }
}

/**
 * Cross-field validator: when userWillUploadReferenceDocuments is false,
 * at least one file must be present (admin must provide reference documents).
 */
function complianceFilesValidator(
  group: AbstractControl,
): ValidationErrors | null {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const userWillUpload = group.get('userWillUploadReferenceDocuments')?.value;
  const files = group.get('files')?.value as
    | AssistantBuilderFileValue[]
    | undefined;

  if (!userWillUpload && (!files || files.length === 0)) {
    return { filesRequired: true };
  }
  return null;
}
